import { Group } from "@mui/icons-material";
import { Button as MUIButton } from "@mui/material";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  getSelectedMatch,
  setSelectedMatch,
  unsetSelectedMatch,
} from "app/reducers/matchesSlice";
import {
  closeModal,
  getModalState,
  MODAL_SECTIONS,
  MODAL_TYPES,
  openModal,
} from "app/reducers/modalSlice";
import {
  getHeadsetStatus,
  getSessionActionState,
  saveHeadsetStatus,
  saveMatches,
  setSession,
  unsetSession,
} from "app/reducers/sessionsSlice";
import { resetFlags } from "app/reducers/sessionsSlice";
import { getSlotState } from "app/reducers/slotsSlice";
import { endMatch } from "app/thunks/matchesAPI";
import { getSessionUpdates } from "app/thunks/sessionsAPI";
import { endGame } from "app/thunks/sessionsAPI";
import { fetchSlots } from "app/thunks/slotsAPI";
import {
  ContainerCard,
  getMatchStateDot,
} from "features/container-card/ContainerCard";
import { NAVIGATOR } from "features/navigation";
import { Match } from "interfaces/Match";
import { SESSION_STATUS } from "interfaces/Sessions";
import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { HeaderWithBack } from "ui";
import { BUTTON_TYPES } from "ui/Button";

import { ConfirmationModal } from "../confirmation-modal/ConfirmationModal";

interface PathParams {
  sessionId: string;
}

export const StartGameScreen = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { sessionId } = useParams<PathParams>();
  const { isOpen, matchId } = useAppSelector(getModalState);
  const { slots } = useAppSelector(getSlotState);
  const { headsetStatus } = useAppSelector(getHeadsetStatus);
  const { session } = useAppSelector(getSessionActionState);
  const [disableFinishMatch, setDisableFinishMatch] = useState(false);
  const [newMatches, setNewMatches] = useState<Match[]>([]);
  const [sessionStatus, setSessionStatus] = useState<SESSION_STATUS>();
  const selectedMatch = useAppSelector(getSelectedMatch);
  const selectedMatchRef = useRef(selectedMatch);
  const sessionRef = useRef(session);
  let intervalId: any;

  const getStatusText = (sessionStatus?: SESSION_STATUS) => {
    switch (sessionStatus) {
      case SESSION_STATUS.READY:
        return "Lista para jugar";
      case SESSION_STATUS.IN_PROGRESS:
        return "En progreso";
      case SESSION_STATUS.COMPLETE:
        return "Completa";
      default:
        return "En espera";
    }
  };

  const getMatchStatus = (sessionStatus?: SESSION_STATUS) => {
    const formattedStatus = getStatusText(sessionStatus);

    return (
      <div className="flex items-center">
        {getMatchStateDot(sessionStatus)}
        <p className="font-normal text-xl">{formattedStatus}</p>
      </div>
    );
  };

  const endGameSession = () => {
    dispatch(
      openModal({
        type: MODAL_TYPES.CONFIRMATION,
        section: MODAL_SECTIONS.SESSION,
      }),
    );
  };

  useEffect(() => {
    headsetStatus &&
      setDisableFinishMatch(headsetStatus?.every((item) => item === "LOBBY"));
  }, [headsetStatus]);

  useEffect(() => {
    selectedMatchRef.current = selectedMatch;
    sessionRef.current = session;
  }, [selectedMatch, session]);

  useEffect(() => {
    if (!sessionId) return;

    const intervalId = setInterval(() => {
      dispatch(getSessionUpdates([sessionId]))
        .then((res) => {
          const sessionData = res.payload.data[0];

          if (sessionId !== sessionData._id) return;

          const updatedHeadsetMatches = sessionData.matches.map(
            (match: Match) => match.headset[0]?.status,
          );
          dispatch(saveHeadsetStatus(updatedHeadsetMatches));

          if (sessionData) {
            const { matches, status } = sessionData;

            if (sessionStatus !== status) setSessionStatus(status);

            if (!sessionRef.current) {
              dispatch(setSession(sessionData));
            }

            const hasMatchesChanged = matches.some(
              (match: Match, index: number) => {
                const previousMatch = newMatches[index];

                const hasChanged =
                  !previousMatch ||
                  (match.headset[0]?.connectionId !==
                    previousMatch.headset[0]?.connectionId &&
                    match.headset[0]?.connectionId === "") ||
                  match?.logs.length !== previousMatch?.logs.length;

                return hasChanged;
              },
            );
            if (
              JSON.stringify(newMatches) !== JSON.stringify(matches) &&
              hasMatchesChanged
            ) {
              dispatch(saveMatches(matches));
              setNewMatches(matches);
            }

            if (selectedMatchRef.current && hasMatchesChanged) {
              const foundMatch = matches.find(
                ({ _id }: Match) =>
                  _id === selectedMatchRef.current?.selectedMatch?._id,
              );
              dispatch(setSelectedMatch(foundMatch));
            }
          }
        })
        .catch((ex) => {
          console.log("There was an error loading the page", ex);
        });
    }, 1000);

    return () => {
      clearInterval(intervalId);
      dispatch(unsetSelectedMatch());
      dispatch(unsetSession());

      // Clear any residual intervals
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      const interval_id = window.setInterval(() => {}, Number.MAX_SAFE_INTEGER);
      for (let i = 1; i < interval_id; i++) {
        window.clearInterval(i);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, sessionId, location.pathname, sessionStatus]);

  const sessionGameStatus = session?.matches.every(
    (match: any) => match.status === SESSION_STATUS.COMPLETE,
  );

  useEffect(() => {
    dispatch(fetchSlots("true"));
  }, [dispatch, sessionId]);

  useEffect(() => {
    if (sessionGameStatus) {
      history.push(NAVIGATOR.matches);
    }
  }, [history, sessionGameStatus]);

  const callbackConfirmModal = async () => {
    if (sessionId) {
      if (matchId) {
        dispatch(endMatch(matchId));
        return dispatch(closeModal());
      }
      clearInterval(intervalId);
      dispatch(resetFlags());
      await dispatch(endGame({ sessionId })).unwrap();
      dispatch(closeModal());
      return history.push(NAVIGATOR.matches);
    }
  };

  const handleBack = () => {
    history.push(NAVIGATOR.sessions);
  };

  return (
    <>
      <div className="flex flex-col w-full min-h-screen px-8 pt-16 pb-8 bg-faux-dark-gunmetal">
        <HeaderWithBack
          text="Seguimiento de partida"
          onBackClick={handleBack}
          className="pb-12"
        />
        <p className="font-semibold text-2xl pb-4">
          {session?.config.sessionNumber}
        </p>
        <div className="flex justify-between pb-6">
          {getMatchStatus(sessionStatus)}
          <MUIButton
            className="px-8"
            variant="contained"
            disabled={disableFinishMatch}
            onClick={endGameSession}
            startIcon={<Group />}
          >
            {`Finalizar partida(s)`}
          </MUIButton>
        </div>

        <div className="flex flex-row flex-wrap justify-center flex-1 gap-8 pb-10">
          {React.Children.toArray(
            newMatches?.map((match) => {
              const currentSlotNumber = slots.find(
                (slot) => slot.deviceId === match.headsetId,
              )?.number;

              return (
                <ContainerCard
                  containerNumber={match?.container[0]?.number}
                  connectionState={!!match?.headset[0]?.connectionId}
                  playerName={match?.playerAlias}
                  matchState={match.status}
                  matchId={match._id}
                  headsetName={`Casco ${currentSlotNumber}`}
                  battery={match?.headset[0].battery}
                  onClick={() => dispatch(setSelectedMatch(match))}
                />
              );
            }),
          )}
        </div>
      </div>
      {isOpen && (
        <ConfirmationModal
          closeButtonType={BUTTON_TYPES.CANCEL}
          confirmButtonType={BUTTON_TYPES.CONFIRM}
          closeText="Cancelar"
          confirmText="Confirmar"
          label={
            matchId
              ? "¿Realmente desea finalizar la partida para este jugador?"
              : "¿Realmente desea finalizar todas las partidas de esta sesión?"
          }
          title={matchId ? "Finalizar partida" : "Finalizar partida(s)"}
          callback={callbackConfirmModal}
        />
      )}
    </>
  );
};
